'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Environment } from '@/lib/environments/types';
import { cn } from '@/lib/utils';
import {
  Server,
  Shield,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';

interface EnvironmentCardProps {
  environment: Environment;
  deploymentCount?: number;
  variant?: 'grid' | 'list';
  onClick?: () => void;
}

const TYPE_COLORS: Record<string, string> = {
  development: 'bg-blue-500',
  staging: 'bg-yellow-500',
  production: 'bg-green-500',
  custom: 'bg-purple-500',
};

const STATUS_ICONS = {
  active: CheckCircle2,
  maintenance: Clock,
  deprecated: AlertCircle,
};

export function EnvironmentCard({
  environment,
  deploymentCount = 0,
  variant = 'grid',
  onClick,
}: EnvironmentCardProps) {
  const StatusIcon = STATUS_ICONS[environment.status] || CheckCircle2;

  if (variant === 'list') {
    return (
      <Card
        className="cursor-pointer hover:border-primary/50 transition-colors"
        onClick={onClick}
      >
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            {/* Color indicator */}
            <div
              className="size-3 rounded-full"
              style={{ backgroundColor: environment.color }}
            />

            {/* Name and type */}
            <div>
              <h3 className="font-medium">
                {environment.display_name || environment.name}
              </h3>
              <p className="text-sm text-muted-foreground">
                {environment.description || environment.type}
              </p>
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {environment.is_protected && (
              <Badge variant="secondary" className="gap-1">
                <Shield className="size-3" />
                Protected
              </Badge>
            )}
            <Badge
              variant="outline"
              className={cn(
                'gap-1',
                environment.status === 'active' && 'border-green-500 text-green-500',
                environment.status === 'maintenance' && 'border-yellow-500 text-yellow-500',
                environment.status === 'deprecated' && 'border-red-500 text-red-500'
              )}
            >
              <StatusIcon className="size-3" />
              {environment.status}
            </Badge>
            <Badge variant="secondary">{environment.type}</Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors group"
      onClick={onClick}
    >
      <CardContent className="pt-6">
        {/* Header with color indicator */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="size-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: environment.color + '20' }}
            >
              <Server
                className="size-5"
                style={{ color: environment.color }}
              />
            </div>
            <div>
              <h3 className="font-medium group-hover:text-primary transition-colors">
                {environment.display_name || environment.name}
              </h3>
              <Badge
                variant="secondary"
                className={cn(
                  'text-xs mt-1',
                  TYPE_COLORS[environment.type] && 'text-white',
                  TYPE_COLORS[environment.type]
                )}
              >
                {environment.type}
              </Badge>
            </div>
          </div>

          {/* Status indicator */}
          <div
            className={cn(
              'size-2.5 rounded-full',
              environment.status === 'active' && 'bg-green-500',
              environment.status === 'maintenance' && 'bg-yellow-500',
              environment.status === 'deprecated' && 'bg-red-500'
            )}
            title={environment.status}
          />
        </div>

        {/* Description */}
        {environment.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {environment.description}
          </p>
        )}

        {/* Footer badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {environment.is_protected && (
            <Badge variant="outline" className="gap-1 text-xs">
              <Shield className="size-3" />
              Protected
            </Badge>
          )}
          {environment.requires_approval && (
            <Badge variant="outline" className="gap-1 text-xs">
              <Clock className="size-3" />
              Approval Required
            </Badge>
          )}
          {environment.is_default && (
            <Badge variant="default" className="text-xs">
              Default
            </Badge>
          )}
          {deploymentCount > 0 && (
            <Badge variant="secondary" className="text-xs ml-auto">
              {deploymentCount} deployment{deploymentCount !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
